import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Filter } from "lucide-react";
import { Product, Category } from "../types";
import { dataService } from "../services/dataService";
import ProductCard from "../components/ProductCard";
import PlayfulLoader from "../components/PlayfulLoader";

export const CategoryPage: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const [products, setProducts] = useState<Product[]>([]);
  const [category, setCategory] = useState<Category | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [filters, setFilters] = useState({
    sortBy: "name" as "name" | "price" | "rating" | "createdAt",
    sortOrder: "asc" as "asc" | "desc",
  });

  useEffect(() => {
    const loadData = async () => {
      if (!categoryId) return;

      try {
        setIsLoading(true);
        const [productsResponse, categoriesResponse] = await Promise.all([
          dataService.getProducts({
            filters: { category: categoryId },
            sortBy: filters.sortBy,
            sortOrder: filters.sortOrder,
          }),
          dataService.getCategories(),
        ]);

        if (productsResponse.success) {
          setProducts(productsResponse.data!);
        }
        if (categoriesResponse.success) {
          const foundCategory = categoriesResponse.data!.find(
            (c) => c.id === categoryId
          );
          setCategory(foundCategory || null);
        }
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [categoryId, filters]);

  const handleSortChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  if (isLoading) {
    return (
      <PlayfulLoader
        size="lg"
        variant="bounce"
        message="🎯 Finding amazing toys in this category..."
        fullScreen
      />
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🔍</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Category Not Found
          </h1>
          <p className="text-gray-600 mb-4">
            The category you're looking for doesn't exist.
          </p>
          <Link
            to="/products"
            className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-2 px-6 rounded-lg transition-all duration-200"
          >
            Browse All Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warm-cream-50">
      {/* Breadcrumbs */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center space-x-2 text-sm">
            <Link
              to="/"
              className="text-purple-600 hover:text-purple-700 font-medium"
            >
              Home
            </Link>
            <span className="text-gray-400">→</span>
            <Link
              to="/products"
              className="text-purple-600 hover:text-purple-700 font-medium"
            >
              Products
            </Link>
            <span className="text-gray-400">→</span>
            <span className="text-gray-900 font-medium">{category.name}</span>
          </nav>
        </div>
      </div>

      {/* Category Header */}
      <div className="bg-gradient-to-r from-soft-purple-200 via-soft-pink-100 to-soft-blue-100 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-6xl mb-4">{category.icon}</div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {category.name}
          </h1>
          <p className="text-xl text-gray-700 max-w-2xl mx-auto mb-6">
            {category.description}
          </p>
          <div className="inline-flex items-center bg-white bg-opacity-80 backdrop-blur-sm rounded-full px-6 py-3">
            <span className="text-gray-700 font-semibold">
              {products.length} {products.length === 1 ? "item" : "items"}{" "}
              available
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <div className="lg:w-1/4">
            <div className="bg-white rounded-xl shadow-lg p-6 sticky top-8">
              <div className="flex items-center mb-6">
                <Filter className="h-5 w-5 text-purple-500 mr-2" />
                <h3 className="text-lg font-bold text-gray-900">
                  Sort & Filter
                </h3>
              </div>

              {/* Sort Options */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sort By
                </label>
                <select
                  value={filters.sortBy}
                  onChange={(e) => handleSortChange("sortBy", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent mb-2"
                >
                  <option value="name">Name</option>
                  <option value="price">Price</option>
                  <option value="rating">Rating</option>
                  <option value="createdAt">Newest</option>
                </select>
                <select
                  value={filters.sortOrder}
                  onChange={(e) =>
                    handleSortChange("sortOrder", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </select>
              </div>

              {/* Back to Products */}
              <Link
                to="/products"
                className="w-full py-2 px-4 bg-gray-500 hover:bg-gray-600 text-white font-semibold rounded-lg transition-colors duration-200 flex items-center justify-center"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                All Products
              </Link>
            </div>
          </div>

          {/* Products Grid */}
          <div className="lg:w-3/4">
            {/* Results Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {products.length} Products in {category.name}
                </h2>
              </div>
            </div>

            {/* Products Grid */}
            {products.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    showShareButton={true}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="text-6xl mb-4">🎯</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  No products in this category
                </h3>
                <p className="text-gray-600 mb-4">
                  We're working on adding more {category.name.toLowerCase()} to
                  our collection!
                </p>
                <Link
                  to="/products"
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-2 px-6 rounded-lg transition-all duration-200"
                >
                  Browse All Products
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryPage;
